import moment, { Moment } from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectId, TaskId } from 'api/models';
import { Sprint } from 'api/sprint-planning/types';
import { FieldOption, FieldWithValue } from 'api/tasks';
import { Button as ButtonAtom } from 'components/atoms/button';
import { DatePicker } from 'components/atoms/date-picker';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { HumanisedDate, humanise } from 'components/atoms/humanised-date';
import { Tag } from 'components/atoms/tag';
import { RbacProgressBar } from 'components/organisms/rbac/components/rbac-default-task-fields';
import { CreatedBy } from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/created-by';
import {
  Control,
  HorizontallyPaddedContent,
  ProgressBarContainer,
  Select,
} from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/styled';
import {
  Content,
  ContentWithOverflow,
  FieldGroupLabel,
  FieldGroupRow,
  FieldLabel,
  GhostFields,
  LabelContainer,
  Left,
  Right,
  Row,
} from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { PLACEHOLDER } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields/fields/constant';
import { NumberField } from 'components/organisms/tasks/fields';
import { useEffortField, usePriorityField } from 'data/fields/hooks';
import { useProjectDetail } from 'data/projects';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { SwitchContentBasedOnAccess } from 'hooks/rbac/withAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { useGetSprintConfiguration } from 'queries/sprint-planning';
import { getRandomColor } from 'utils/get-random-color';
import { getFieldRenderer } from '../task-custom-fields';
import { EffortDurationField } from './effort-as-duration';

interface DefaultTaskFieldsProps {
  progress?: number;
  effort?: number;
  priority?: FieldOption<number>;
  currentSprint?: Pick<Sprint, 'sprintId' | 'sprintName'>;
  associatedSprints?: Pick<Sprint, 'sprintId' | 'sprintName'>[];
  taskId: TaskId;
  createdBy: number;
  createdAt: number;
  completedAt?: number | null;
  startDateActual?: string;
  fieldsWithValues?: FieldWithValue[];
}

const EffortButton = styled(ButtonAtom)`
  width: 100%;
`;

export const DefaultTaskFields: FC<DefaultTaskFieldsProps> = (props) => {
  const {
    effort,
    progress = 0,
    priority,
    createdBy,
    createdAt,
    completedAt,
    taskId: [, projectId, taskId],
    startDateActual,
    fieldsWithValues,
    currentSprint,
    associatedSprints,
  } = props;

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );

  const priorityField = usePriorityField();
  const effortField = useEffortField();
  const taskCreatedBy = useGetUserFromIndexedDb(createdBy);

  const dispatch = useDispatch();
  const updateTask = (data) => {
    dispatch(updateTaskSagaAction({ projectId, taskId, data }));
  };

  const updateEffort = (value: number | null) => updateTask({ effort: value });

  const updateProgress = (value: number | string) =>
    updateTask({ progress: Number(value) });

  const [progressValue, setProgressValue] = useState<number>(progress);

  useEffect(() => {
    setProgressValue(progress);
  }, [progress]);

  const [priorityValue, setPriorityValue] =
    useState<FieldOption<number> | undefined>();

  useEffect(() => {
    setPriorityValue(priority);
  }, [priority]);

  const [completedAtState, setCompletedAt] =
    useState<Moment | null | undefined>();

  useEffect(() => {
    setCompletedAt(completedAt == null ? null : moment(completedAt));
  }, [completedAt]);

  const updateCompletedAt = (value: Moment | null) => {
    setCompletedAt(value);
    updateTask({ completedAtActual: value?.valueOf() });
  };
  const [startDateActualState, setStartDateActual] =
    useState<Moment | null | undefined>();

  useEffect(() => {
    setStartDateActual(
      startDateActual == null ? null : moment(startDateActual),
    );
  }, [startDateActual]);

  const updateStartDateActual = (value: Moment | null) => {
    setStartDateActual(value);
    updateTask({ startDateActual: value?.format(API_DATE_FORMAT) });
  };
  const { hasAccessToResource } = useWithAccess();
  const isStartDateActualEditable = useMemo(
    () =>
      startDateActualState != null &&
      hasAccessToResource({
        rule: 'TASK_ACTUAL_START:WRITE',
        resourceData: projectResource,
      }),
    [hasAccessToResource, projectResource, startDateActualState],
  );
  const isCompletedAtEditable = useMemo(
    () =>
      completedAtState != null &&
      hasAccessToResource({
        rule: 'TASK_COMPLETED_AT:WRITE',
        resourceData: projectResource,
      }),
    [hasAccessToResource, projectResource, completedAtState],
  );

  const [isSprintPlanningFeatureEnabled] = useAccountFeature(
    AccountFeature.SprintPlanning,
  );
  const { data: sprintConfig } = useGetSprintConfiguration();
  const { pointType: selectedPointType, pointsEnabled } = sprintConfig ?? {};

  const pointFields = fieldsWithValues?.filter(
    (field) => !!field.metaData?.pointType,
  );

  const selectedPointsField = pointFields?.find(
    ({ metaData }) => metaData?.pointType === selectedPointType,
  );

  const selectedPointsFieldWithValue = fieldsWithValues?.find(
    ({ fieldId }) => fieldId === selectedPointsField?.fieldId,
  );

  const FieldRenderer = selectedPointsFieldWithValue
    ? getFieldRenderer(selectedPointsFieldWithValue)
    : null;

  const updatePoints = (fieldValue, { fieldId }: FieldWithValue) => {
    updateTask({ fields: [{ fieldId, fieldValue }] });
  };

  return (
    <GhostFields>
      <FieldGroupRow>
        <Left>
          <LabelContainer>
            <FieldGroupLabel>
              <FormattedMessage id="default" />
            </FieldGroupLabel>
          </LabelContainer>
        </Left>
      </FieldGroupRow>
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="created_by" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.created_by">
          <Content>
            <HorizontallyPaddedContent>
              {taskCreatedBy ? <CreatedBy user={taskCreatedBy} /> : null}
            </HorizontallyPaddedContent>
          </Content>
        </Right>
      </Row>
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="created_on" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.created_on">
          <Content>
            <HorizontallyPaddedContent>
              <LabelContainer>
                <HumanisedDate timestamp={moment(createdAt)} />
              </LabelContainer>
            </HorizontallyPaddedContent>
          </Content>
        </Right>
      </Row>
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="start_date_Actual" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.start_date_actual">
          <Content>
            {isStartDateActualEditable ? (
              <DatePicker
                format={(val: Moment) => humanise(val)}
                value={startDateActualState}
                placeholder={PLACEHOLDER}
                onChange={updateStartDateActual}
                suffixIcon={null}
                data-cy="right.content.date.startDateActual"
                disabledDate={(current: Moment) =>
                  completedAtState ? current.isAfter(completedAtState) : false
                }
                allowClear={false}
              />
            ) : (
              <HorizontallyPaddedContent>
                <LabelContainer>
                  {startDateActualState ? (
                    <HumanisedDate timestamp={startDateActualState} />
                  ) : (
                    PLACEHOLDER
                  )}
                </LabelContainer>
              </HorizontallyPaddedContent>
            )}
          </Content>
        </Right>
      </Row>
      {completedAtState && (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                <FormattedMessage id="completed_on" />
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right data-cy="right.content.completed_on">
            <Content>
              {isCompletedAtEditable ? (
                <DatePicker
                  format={(val: Moment) => humanise(val)}
                  value={completedAtState}
                  placeholder={PLACEHOLDER}
                  onChange={updateCompletedAt}
                  suffixIcon={null}
                  data-cy="right.content.date.completedAt"
                  disabledDate={(current: Moment) =>
                    startDateActualState
                      ? current.isBefore(startDateActualState)
                      : false
                  }
                  allowClear={false}
                />
              ) : (
                <HorizontallyPaddedContent>
                  <LabelContainer>
                    <HumanisedDate timestamp={completedAtState} />
                  </LabelContainer>
                </HorizontallyPaddedContent>
              )}
            </Content>
          </Right>
        </Row>
      )}
      {effortField && (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                <FormattedMessage id="effort" />
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right data-cy="right.content.effort">
            <Content>
              <SwitchContentBasedOnAccess
                rule="TASK_EFFORT:WRITE"
                resourceData={projectResource}
                doesNotHaveAccess={
                  <EffortButton type="text" disabled>
                    {effort}
                  </EffortButton>
                }
                hasAccess={
                  <EffortDurationField
                    onChange={updateEffort}
                    field={effortField as unknown as NumberField}
                    value={effort}
                  />
                }
              />
            </Content>
          </Right>
        </Row>
      )}
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="progress" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.progress">
          <Content>
            <ProgressBarContainer>
              <RbacProgressBar
                value={progressValue}
                onChange={(value) => {
                  setProgressValue(value);
                }}
                onAfterChange={updateProgress}
                onInputPressEnter={(e) => e.currentTarget.blur()}
                onInputBlur={() => updateProgress(progressValue)}
                rule="TASK_PROGRESS:WRITE"
                resourceData={projectResource}
                propsIfNoAccess={{
                  disabled: true,
                }}
              />
            </ProgressBarContainer>
          </Content>
        </Right>
      </Row>
      {priorityField ? (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                <FormattedMessage id="priority" />
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right data-cy="right.content.priority">
            <Content>
              <Select
                isClearable
                isSearchable={false}
                placeholder={PLACEHOLDER}
                options={priorityField.metaData?.options ?? []}
                type="tag"
                defaultValue={priorityField.metaData?.options?.find(
                  (o) => o.value === priorityField.metaData?.default,
                )}
                value={priorityValue || null}
                onChange={(selected) => {
                  setPriorityValue(selected);
                  updateTask({
                    fields: [
                      {
                        fieldId: priorityField.fieldId,
                        fieldValue: selected ? selected.value : null,
                      },
                    ],
                  });
                }}
                components={{
                  DropdownIndicator: null,
                  IndicatorSeparator: null,
                  Control,
                }}
                rule="TASK_PRIORITY:WRITE"
                resourceData={projectResource}
                propsIfNoAccess={{
                  isDisabled: true,
                }}
              />
            </Content>
          </Right>
        </Row>
      ) : null}

      {isSprintPlanningFeatureEnabled && (
        <>
          {pointsEnabled && (
            <Row>
              <Left>
                <LabelContainer>
                  <FieldLabel>
                    <FormattedMessage id="reports.sprint.metrics.points" />
                  </FieldLabel>
                </LabelContainer>
              </Left>
              <Right data-cy="right.content.points">
                <Content>
                  {FieldRenderer ? (
                    <FieldRenderer
                      field={selectedPointsFieldWithValue}
                      onChange={updatePoints}
                    />
                  ) : null}
                </Content>
              </Right>
            </Row>
          )}
          <Row>
            <Left>
              <LabelContainer>
                <FieldLabel>
                  <FormattedMessage id="current_sprint" />
                </FieldLabel>
              </LabelContainer>
            </Left>
            <Right>
              <Content>
                <HorizontallyPaddedContent>
                  {currentSprint ? (
                    <Tag
                      color={getRandomColor(currentSprint?.sprintName ?? '')}
                    >
                      {currentSprint?.sprintName}
                    </Tag>
                  ) : (
                    PLACEHOLDER
                  )}
                </HorizontallyPaddedContent>
              </Content>
            </Right>
          </Row>
          <Row>
            <Left>
              <LabelContainer>
                <FieldLabel>
                  <FormattedMessage id="associated_sprints" />
                </FieldLabel>
              </LabelContainer>
            </Left>
            <Right>
              <HorizontallyPaddedContent>
                {associatedSprints ? (
                  <ContentWithOverflow>
                    {associatedSprints?.map((sprint) => (
                      <Tag color={getRandomColor(sprint.sprintName)}>
                        {sprint.sprintName}
                      </Tag>
                    ))}
                  </ContentWithOverflow>
                ) : (
                  PLACEHOLDER
                )}
              </HorizontallyPaddedContent>
            </Right>
          </Row>
        </>
      )}
    </GhostFields>
  );
};
